/* ==========================================================================
   MICHELANGELO MUSIC — audio preview player
   Built 2026-07-24.

   Upgrades any [data-mm-player] block containing an <audio> element into a
   waveform scrubber. The native <audio> stays as the transport and as the
   no-JS fallback; a transparent range input sits over the waveform so the
   control remains pointer- and keyboard-operable and reports its position to
   assistive technology.

   Deliberately measures nothing. Bars are a fixed count that flex to fill
   whatever width they get, and progress is a CSS clip. An earlier version
   sized the bars from getBoundingClientRect and deadlocked: the container
   starts empty, the theme collapses empty divs to display:none, so it read a
   width of zero and bailed out before it could ever populate itself.
   ========================================================================== */

(() => {
  'use strict';

  const BAR_COUNT = 56;

  const ICONS = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>',
    volume:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4zm12.5 3A4.5 4.5 0 0 0 14 7.97v8.06A4.5 4.5 0 0 0 16.5 12zm0-8.27v2.06a7 7 0 0 1 0 12.42v2.06a9 9 0 0 0 0-16.54z"/></svg>',
    muted:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4zm11.5 3 2.25-2.25 1.5 1.5L17 13.5l2.25 2.25-1.5 1.5L15.5 15l-2.25 2.25-1.5-1.5L14 13.5l-2.25-2.25 1.5-1.5L15.5 12z"/></svg>'
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return minutes + ':' + remainder;
  };

  const spokenTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 1) return '0 seconds';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const parts = [];
    if (minutes) parts.push(minutes + (minutes === 1 ? ' minute' : ' minutes'));
    if (secs) parts.push(secs + (secs === 1 ? ' second' : ' seconds'));
    return parts.join(' ');
  };

  /* A seeded xorshift, so the waveform is identical on every render and every
     reload. Math.random would make the shape jump around. */
  const barHeights = (count) => {
    let state = 0x9e3779b9;
    const next = () => {
      state ^= state << 13;
      state ^= state >>> 17;
      state ^= state << 5;
      return ((state >>> 0) % 1000) / 1000;
    };
    const out = [];
    for (let i = 0; i < count; i += 1) {
      /* Envelope: quieter at the edges, fuller through the middle, so it reads
         as a musical phrase rather than as noise. */
      const position = count > 1 ? i / (count - 1) : 0.5;
      const envelope = 0.55 + 0.45 * Math.sin(Math.PI * position);
      const jitter = 0.35 + next() * 0.65;
      out.push(Math.max(14, Math.round(envelope * jitter * 100)));
    }
    return out;
  };

  const barMarkup = () =>
    barHeights(BAR_COUNT)
      .map((h) => '<span class="mm-player__bar" style="--h:' + h + '%"></span>')
      .join('');

  const pauseOtherMedia = (active) => {
    document.querySelectorAll('audio, video').forEach((media) => {
      if (media !== active && !media.paused) media.pause();
    });
  };

  const upgrade = (root) => {
    if (root.dataset.mmPlayerReady === 'true') return;
    const audio = root.querySelector('audio');
    if (!audio) return;

    root.dataset.mmPlayerReady = 'true';
    audio.controls = false;
    audio.preload = 'metadata';

    const meta = root.querySelector('.mm-player__meta');
    const bars = barMarkup();

    const transport = document.createElement('div');
    transport.className = 'mm-player__transport';
    transport.innerHTML =
      '<button class="mm-player__btn mm-player__btn--toggle" type="button" aria-label="Play preview">' + ICONS.play + '</button>' +
      '<div class="mm-player__scrub">' +
        '<div class="mm-player__wave" aria-hidden="true">' +
          '<div class="mm-player__row mm-player__row--base"><div class="mm-player__bars">' + bars + '</div></div>' +
          '<div class="mm-player__row mm-player__row--played"><div class="mm-player__bars">' + bars + '</div></div>' +
        '</div>' +
        '<input class="mm-player__seek" type="range" min="0" max="1000" step="1" value="0" aria-label="Seek within preview">' +
      '</div>' +
      '<span class="mm-player__time">0:00 / 0:00</span>' +
      '<button class="mm-player__btn mm-player__btn--volume" type="button" aria-label="Mute" aria-pressed="false">' + ICONS.volume + '</button>';
    root.appendChild(transport);

    const toggle = transport.querySelector('.mm-player__btn--toggle');
    const volume = transport.querySelector('.mm-player__btn--volume');
    const timeEl = transport.querySelector('.mm-player__time');
    const seek = transport.querySelector('.mm-player__seek');
    const playedRow = transport.querySelector('.mm-player__row--played');

    const sync = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      const progress = duration ? Math.min(1, audio.currentTime / duration) : 0;
      playedRow.style.setProperty('--mm-progress', progress * 100 + '%');
      if (document.activeElement !== seek) seek.value = Math.round(progress * 1000);
      timeEl.textContent = formatTime(audio.currentTime) + ' / ' + formatTime(duration);
      seek.setAttribute(
        'aria-valuetext',
        spokenTime(audio.currentTime) + ' of ' + spokenTime(duration)
      );
    };

    const setDurationMeta = () => {
      if (meta && !meta.textContent.trim() && Number.isFinite(audio.duration) && audio.duration) {
        meta.textContent = formatTime(audio.duration) + ' preview';
      }
    };

    toggle.addEventListener('click', () => {
      if (audio.paused) {
        const started = audio.play();
        if (started && typeof started.catch === 'function') started.catch(() => {});
      } else {
        audio.pause();
      }
    });

    volume.addEventListener('click', () => {
      audio.muted = !audio.muted;
      volume.innerHTML = audio.muted ? ICONS.muted : ICONS.volume;
      volume.setAttribute('aria-label', audio.muted ? 'Unmute' : 'Mute');
      volume.setAttribute('aria-pressed', String(audio.muted));
    });

    seek.addEventListener('input', () => {
      if (!Number.isFinite(audio.duration) || !audio.duration) return;
      audio.currentTime = (Number(seek.value) / 1000) * audio.duration;
      sync();
    });

    audio.addEventListener('play', () => {
      toggle.innerHTML = ICONS.pause;
      toggle.setAttribute('aria-label', 'Pause preview');
    });

    audio.addEventListener('pause', () => {
      toggle.innerHTML = ICONS.play;
      toggle.setAttribute('aria-label', 'Play preview');
    });

    ['timeupdate', 'loadedmetadata', 'durationchange', 'ended', 'seeked'].forEach((evt) =>
      audio.addEventListener(evt, sync)
    );
    audio.addEventListener('loadedmetadata', setDurationMeta);
    audio.addEventListener('durationchange', setDurationMeta);

    setDurationMeta();
    sync();
  };

  const initialize = () => {
    document.querySelectorAll('[data-mm-player]').forEach(upgrade);
  };

  /* One preview at a time, and never competing with the product videos. */
  document.addEventListener(
    'play',
    (event) => {
      if (event.target instanceof HTMLMediaElement) pauseOtherMedia(event.target);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', initialize);
})();